import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Financial Growth Strategy) ---
import {
    // Core Concepts & General
    faChartLine,        // Growth / Strategy / Performance
    faDollarSign,       // Financial / Revenue / Profitability
    faLightbulb,        // Strategy / Innovation / Opportunities
         // Goals / Objectives / Targeting
    faUsers,            // Market / Community / Patients
    faBuilding,         // Facility / Hospital
    faCheckCircle,      // Success / Viability
    // Primary Areas
    faCubes,            // New Product Development (Building blocks)
    faExpandArrowsAlt,  // Market Expansion
    // 5 Key Strategies
    faBullseye,         // Market Penetration (Reused - hitting existing target harder)
    faMapMarkedAlt,     // Market Expansion (Geographic)
    faBoxOpen,          // Product Expansion (New offerings)
    faSitemap,          // Diversification (Branching out)
    faHandshake,        // Strategic Partnerships / Collaborations
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faCalculator, faMoneyCheckAlt, faFileInvoiceDollar, faTasks, faBalanceScale, faExchangeAlt,
     faChartPie, faReceipt, faArrowsAltH, faUsersCog, faFileExcel, faTachometerAlt, faCogs,
     faLandmark, faSearch, faFileSignature, faPlayCircle, faClipboardList, faBinoculars,
     faSyncAlt, faUserTie
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Strategy Essential?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Two Primary Areas (Title optional)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Approach (5 Strategies)

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for growth/strategy/finance
    const heroBgImage = 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Business strategy/collaboration image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Comprehensive Financial Growth Strategy</h1>
            <p className="cliniqor-sub-heading">
              Ensuring Healthcare Facilities Thrive with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              A robust strategy is critical for survival and success. Cliniqor helps craft proactive plans for sustainable growth through new product development and market expansion, ensuring long-term financial health.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY STRATEGY IS ESSENTIAL --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why a Financial Growth Strategy is Essential</h2>
              <p>Staying ahead, meeting evolving needs, and ensuring sustainability.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Healthcare facilities must align strategy with current financial realities and future trends. Without a long-term plan, hospitals risk plateauing in patient volume, revenue, and service quality.</p>
                     <p>Cliniqor specializes in creating customized strategies assessing your current situation, competition, and market trends to shape proactive, sustainable plans beneficial to your facility's success.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- TWO PRIMARY AREAS (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            {/* Optional Title for this section */}
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Core Growth Strategies</h2>
                 <p>Focused approaches for expanding and diversifying healthcare reach.</p>
            </div>
            <div className="cliniqor-features-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}> {/* Adjusted grid for 2 items */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCubes} /></div>
                    <h4>1. New Product Development</h4>
                    <p>Launching new services/departments (oncology, pediatrics), adding specialist doctors, or upgrading technology (robotics, AI diagnostics) to enhance care and attract patients.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExpandArrowsAlt} /></div>
                    <h4>2. Market Expansion</h4>
                    <p>Targeting underserved geographic areas (rural, smaller towns) by opening new branches, specialized clinics, or outpatient centers to increase patient base and fill healthcare gaps.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S APPROACH (5 Strategies - Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor’s Approach to Financial Growth Strategy Development</h2>
               <p>Tailored, actionable plans identifying opportunities and positioning for success.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginBottom: '30px' }}>
                 <p>Our process starts with in-depth analysis of operations, market position, financials, and demographics to provide solutions fitting your goals.</p>
            </div>
            {/* Using Planning Items for the 5 Strategies */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullseye} /></div>
                    <div className="cliniqor-content"><h4>1. Market Penetration Strategy</h4> <p>Growing presence in existing markets via new/improved services, rebranding, marketing, and patient retention.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <div className="cliniqor-content"><h4>2. Market Expansion Strategy</h4> <p>Moving into new geographic areas (towns, rural regions) by opening clinics or centers based on research.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBoxOpen} /></div>
                    <div className="cliniqor-content"><h4>3. Product Expansion Strategy</h4> <p>Adding advanced diagnostics, surgical specialties, or wellness services to attract wider patient base.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>4. Diversification Strategy</h4> <p>Expanding geographically AND through new services simultaneously to mitigate financial risks.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandshake} /></div>
                    <div className="cliniqor-content"><h4>5. Strategic Partnerships & Collaborations</h4> <p>Partnering with research institutions, pharma, tech providers for shared resources and innovation.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       {/* --- CONCLUSION SECTION (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              {/* Removed title4Ref as this is the final content section before footer */}
              <div className="cliniqor-section-title cliniqor-fade-in-up">
                 <h2>Empowering Hospitals for Long-Term Success</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Financial growth requires continuous planning, market awareness, and investment. Cliniqor helps formulate strategies aligned with your goals and market needs.</p>
                   <p>By expanding services, penetrating markets, and forming partnerships, hospitals can grow sustainably, improve care, and increase profitability. Let Cliniqor guide your growth journey to thrive in an ever-changing healthcare environment.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Chart Your Hospital's Growth Path</h3>
          <p>Ensure your healthcare facility not only survives but excels. Partner with Cliniqor for tailored financial growth strategies designed for sustainable success.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Growth Strategy Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;